/**
 * FHIR Connector configuration component.
 * Specialized configuration for FHIR connector (SMART on FHIR / Standard OAuth2).
 */

// React Imports
import { useState } from 'react';
import { CoordioDropdown, Input, Switch } from '@coordio/ui';
import { OAuthConnectButton } from './OAuthConnectButton';

export interface FhirConnectorConfigProps {
  instanceId?: string;
  settingsJson?: string;
  isConnected?: boolean;
  onChange?: (settingsJson: string) => void;
  onConnected?: () => void;
}

/**
 * FHIR Connector configuration component.
 */
export const FhirConnectorConfig = ({
  instanceId,
  settingsJson = '{}',
  isConnected = false,
  onChange,
  onConnected,
}: FhirConnectorConfigProps) => {
  const [serverType, setServerType] = useState<'smart' | 'standard'>('smart');
  const [baseUrl, setBaseUrl] = useState('');
  const [fhirVersion, setFhirVersion] = useState('R4');
  const [hipaaAcknowledged, setHipaaAcknowledged] = useState(false);

  const handleServerTypeChange = (newType: 'smart' | 'standard') => {
    setServerType(newType);
    const settings = JSON.parse(settingsJson || '{}');
    settings.serverType = newType;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          FHIR Server Configuration
        </h6>

        <div className="mb-6">
          <div className="space-y-1 mb-4">
            <label className="text-sm font-medium">FHIR Server Base URL</label>
            <Input
              type="text"
              className="w-full"
              value={baseUrl}
              required
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setBaseUrl(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.baseUrl = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
            />
            <p className="text-xs text-muted-foreground">Base URL of the FHIR server (e.g., https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4)</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">FHIR Version</label>
            <CoordioDropdown
              options={[
                { value: 'R4', label: 'R4' },
                { value: 'STU3', label: 'STU3' },
                { value: 'DSTU2', label: 'DSTU2' },
              ]}
              value={fhirVersion}
              onChange={(val) => {
                const v = val ?? 'R4';
                setFhirVersion(v);
                const settings = JSON.parse(settingsJson || '{}');
                settings.fhirVersion = v;
                onChange?.(JSON.stringify(settings));
              }}
              placeholder="Select FHIR version"
              size="sm"
              clearable={false}
            />
          </div>
        </div>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Server Type
          </p>
          <div className="inline-flex w-full rounded-md border" role="group">
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-r transition-colors rounded-l-md ${
                serverType === 'smart' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleServerTypeChange('smart')}
            >
              SMART on FHIR
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-r-md ${
                serverType === 'standard' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleServerTypeChange('standard')}
            >
              Standard OAuth2
            </button>
          </div>
        </div>

        {serverType === 'smart' && (
          <div className="flex flex-col gap-4">
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="FHIR Server"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              SMART on FHIR requires OAuth 2.0 with healthcare-specific scopes (e.g., patient/*.read, user/*.read).
            </div>
          </div>
        )}

        {serverType === 'standard' && (
          <div className="flex flex-col gap-4">
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="FHIR Server"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              Standard OAuth2 authentication for generic FHIR servers.
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-border">
          <h6 className="text-lg font-semibold tracking-tight mb-4">
            Compliance &amp; Settings
          </h6>
          <label className="flex items-start gap-3">
            <Switch
              checked={hipaaAcknowledged}
              onCheckedChange={(checked) => {
                setHipaaAcknowledged(checked as boolean);
                const settings = JSON.parse(settingsJson || '{}');
                settings.hipaaAcknowledged = checked;
                onChange?.(JSON.stringify(settings));
              }}
            />
            <div>
              <p className="text-sm">HIPAA Compliance Acknowledgment</p>
              <p className="text-xs text-muted-foreground">
                I acknowledge that this connector will access protected health information (PHI) and will comply with HIPAA requirements.
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>
  );
};
